/**
 * Shared axios client (Phase 6).
 *
 * Access token lives in memory (set by AuthContext). Cookies go with
 * withCredentials for /api/auth/refresh and /api/auth/logout.
 * On 401, one shared refresh promise retries the original request.
 */
import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let accessToken = null;
let handlers = { onUser: null, onLogout: null };

export function setAccessToken(token) {
  accessToken = token || null;
}

export function getAccessToken() {
  return accessToken;
}

export function bindAuthHandlers({ onUser, onLogout }) {
  handlers.onUser = onUser;
  handlers.onLogout = onLogout;
}

export function safeReturnTo(raw) {
  if (!raw || typeof raw !== 'string') return '/';
  const path = raw.trim();
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return '/';
  if (path.includes('://') || path.includes('\\')) return '/';
  if (path.startsWith('/auth/')) return '/';
  return path.slice(0, 512);
}

function isAuthUrl(url = '') {
  return /\/api\/auth\/(login|exchange|refresh|logout)(?:\?|$)/.test(String(url));
}

let refreshPromise = null;

export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/api/auth/refresh', {}, { skipAuthRefresh: true })
      .then((res) => {
        const token = res.data?.access_token;
        const user = res.data?.user;
        if (!token || !user) {
          throw new Error('refresh incomplete');
        }
        setAccessToken(token);
        if (handlers.onUser) handlers.onUser(user);
        return res.data;
      })
      .catch((err) => {
        setAccessToken(null);
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status === 401 &&
      !original._retry &&
      !original.skipAuthRefresh &&
      !isAuthUrl(original.url || '')
    ) {
      original._retry = true;
      try {
        await refreshSession();
        original.headers = original.headers || {};
        if (accessToken) {
          original.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(original);
      } catch {
        setAccessToken(null);
        if (handlers.onLogout) handlers.onLogout();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

export default api;